import type { DateRangeType } from '@g17eco/types/common';
import { formatDateUTC } from '@utils/date';
import sanitize from 'sanitize-filename';

interface FilenameProps {
  initiativeName?: string;
  effectiveDate: string;
  _id: string;
  type?: string;
}

export interface FilenameContext {
  name: string;
  subsidiary?: string;
  dateRange?: DateRangeType;
  singleDate?: Date | string;
  type?: string;
  extension?: string;
}

/**
 * Centralized service for generating file-safe, descriptive filenames
 * with consistent formatting across the application
 */
export class FilenameService {
  /**
   * Maximum filename length (most file systems support 255 characters)
   * Reserve some space for extension and safety margin
   */
  private static readonly MAX_FILENAME_LENGTH = 200;
  private static readonly MIN_SUBSIDIARY_LENGTH = 50;
  private static readonly MIN_NAME_LENGTH = 50;

  private static formatDate(date: Date | string): string {
    return formatDateUTC(date, 'DD-MMM-YYYY');
  }

  private static formatDateRange(startDate: Date | string, endDate: Date | string): string {
    const start = this.formatDate(startDate);
    const end = this.formatDate(endDate);
    return `${start}-to-${end}`;
  }

  private static getTotalLength = ({
    subsidiary,
    name,
    dateStr,
  }: {
    subsidiary: string;
    name: string;
    dateStr: string;
  }) => {
    // one separator (underscore) between parts => 2 separators total
    return subsidiary.length + dateStr.length + name.length + 2;
  };

  private static truncate = (value: string, max: number) => (value.length > max ? value.slice(0, max) : value);

  private static calculateAvailableSpace({ remainingParts }: { remainingParts: string[] }): number {
    const usedSpace = remainingParts.reduce((sum, part) => sum + part.length, 0);
    return this.MAX_FILENAME_LENGTH - usedSpace - 2;
  }

  static buildFilename(context: FilenameContext): string {
    let subsidiary = context.subsidiary || '';
    let name = context.name;

    const dateStr = context.dateRange
      ? this.formatDateRange(context.dateRange.startDate ?? '', context.dateRange.endDate ?? '')
      : context.singleDate
        ? this.formatDate(context.singleDate)
        : '';

    // Stage 1 — name
    if (this.getTotalLength({ subsidiary, name, dateStr }) > this.MAX_FILENAME_LENGTH) {
      const available = this.calculateAvailableSpace({
        remainingParts: [subsidiary, dateStr],
      });
      name = this.truncate(name, Math.max(this.MIN_NAME_LENGTH, available));
    }

    // Stage 2 — subsidiary
    if (this.getTotalLength({ subsidiary, name, dateStr }) > this.MAX_FILENAME_LENGTH) {
      const available = this.calculateAvailableSpace({
        remainingParts: [dateStr, name],
      });
      subsidiary = this.truncate(subsidiary, Math.max(this.MIN_SUBSIDIARY_LENGTH, available));
    }

    return sanitize([subsidiary, dateStr, name].filter(Boolean).join('_'));
  }

  static generate(context: FilenameContext): string {
    const baseFilename = this.buildFilename(context);
    const extension = context.extension || '';
    return extension ? `${baseFilename}.${extension}` : baseFilename;
  }

  static forPDF({ name, subsidiary, dateRange }: Pick<FilenameContext, 'name' | 'subsidiary' | 'dateRange'>): string {
    return this.generate({
      name,
      subsidiary,
      dateRange,
      extension: 'pdf',
    });
  }

  static forExcel({ name, subsidiary, singleDate }: Pick<FilenameContext, 'name' | 'subsidiary' | 'singleDate'>): string {
    return this.generate({
      name,
      subsidiary,
      singleDate: singleDate || new Date(),
      extension: 'xlsx',
    });
  }

  static forCSV({ name, subsidiary, singleDate }: Pick<FilenameContext, 'name' | 'subsidiary' | 'singleDate'>): string {
    return this.generate({
      name,
      subsidiary,
      singleDate: singleDate || new Date(),
      extension: 'csv',
    });
  }

  static forWord({ name, subsidiary, dateRange }: Pick<FilenameContext, 'name' | 'subsidiary' | 'dateRange'>): string {
    return this.generate({
      name,
      subsidiary,
      dateRange,
      extension: 'docx',
    });
  }

  /**
   * @deprecated Use more specific methods (forPDF, forExcel, etc.) for new code
   * Legacy function maintained for backward compatibility with existing report-output usage
   */
  static getLegacyFilename({ initiativeName, effectiveDate, _id, type }: FilenameProps) {
    const nameParts = [];
    if (initiativeName) {
      nameParts.push(initiativeName);
    }

    if (effectiveDate) {
      nameParts.push(formatDateUTC(effectiveDate, 'YYYY MMMM'));
    }

    if (type) {
      nameParts.push(type);
    }

    // Fallback to id
    if (nameParts.length === 0) {
      nameParts.push('(' + String(_id) + ')');
    }

    return sanitize(nameParts.join(' '));
  }
}
